
export enum FeatureType {
  Scan = 'Scan',
  GapFinder = 'GapFinder',
  LaunchMath = 'LaunchMath',
  LocaleLaunchpad = 'LocaleLaunchpad',
  ReverseDiscovery = 'ReverseDiscovery',
  DensityScout = 'DensityScout',
  Optimize = 'Optimize',
  GrassrootsIdeator = 'GrassrootsIdeator'
}

export interface FinancialPath {
  startupCost: string;
  startupCostValue?: number; // Numeric for charts
  runway12Mo: string;
  breakEvenTimeline: string;
  breakEvenMonths?: number; // Numeric for charts
  scalingSteps?: string[];
  entryStrategy?: string;
}

export interface RiskGuardAnalysis {
  score: number; // 1-10
  financial: string; 
  market: string; 
  regulatory: string;
}

export interface CompetitorComparison {
  nearestCompetitor: string;
  distance: string;
  competitorCapacity: string;
  ourAdvantage: string;
}

export interface LocationSuggestion {
  name: string;
  type: 'city' | 'neighborhood' | 'district';
  reason: string;
}

export interface SiteRecommendation {
  name: string;
  type: 'street' | 'building' | 'corridor' | 'intersection';
  reason: string;
  coordinates?: { lat: number; lng: number };
}

export interface SpottedEntity {
  name: string;
  address?: string;
  contact?: string;
  description?: string;
}

export interface HeatmapPoint {
  lat: number;
  lng: number;
  weight: number; // 0-1 density weight
  label?: string; // Optional neighborhood or street name
}

export interface IntelligenceHubData {
  competitorDeepDive: string;
  differentiationEngine: string;
  marketShareEstimation: string;
  seasonalityForecasting: string;
  gentrificationTrajectory: string;
  economicCycleResilience: string;
  personaGenerator: string;
  footTrafficPatterns: string;
  sentimentAnalysis: string;
}

export interface OperationsHubData {
  scenarioModeling: string;
  investorMatchmaking: string;
  grantScanner: string;
  laborMarketAnalytics: string;
  supplyChainMapping: string;
  regulatoryNavigator: string;
  performanceBenchmarking: string;
  expansionScouting: string;
  exitStrategyModeling: string;
}

export interface CollaborationHubData {
  stakeholderSharing: string;
  versionControl: string;
  commentaryAnnotations: string;
  algorithmicTransparency: string;
  biasDetection: string;
  dataPrivacyControls: string;
  crmConnectors: string;
  erpIntegrations: string;
  apiAccess: string;
  ruralMarketOptimization: string;
  fullLocalization: string;
  advancedOfflineMode: string;
}

export interface LaunchMathDeepDive {
  startupCapital: string;
  monthlyOpEx: string;
  revenueStreams: string;
  breakEvenAnalysis: string;
}

export interface SiteReconDeepDive {
  microLocationFit: string;
  accessibilityLogistics: string;
  demographicAlignment: string;
  visualLandscape: string;
}

export interface ScalingStrategyDeepDive {
  first90Days: { week: string; milestone: string; howTo: string }[];
  quarterlyScaling: { period: string; goal: string; howTo: string }[];
  trendAnalysis: { trend: string; description: string; impact: string }[];
  pivotPoints: { scenario: string; strategy: string }[];
}

export interface RiskGuardDeepDive {
  regulatoryZoning: string;
  marketSaturation: string;
  operationalSupplyChain: string;
  financialStressTest: {
    bestCase: string;
    expectedCase: string;
    worstCase: string;
  };
  mitigationRoadmap: string;
}

export interface Comment {
  id: string;
  userId: string;
  userName: string;
  text: string;
  timestamp: number;
  parentId?: string; // For nested replies
}

export interface BusinessIdeaSnapshot {
  id: string;
  timestamp: number;
  label: string;
  data: Partial<BusinessIdea>;
}

export interface VentureActuals {
  revenue: { period: string; value: number }[];
  expenses: { period: string; value: number }[];
  customers: { period: string; value: number }[];
}

export interface IntegrationStatus {
  salesforce?: { connected: boolean; lastSync?: number };
  hubspot?: { connected: boolean; lastSync?: number };
  sap?: { connected: boolean; lastSync?: number };
}

export interface SwotElement {
  id: string;
  text: string;
  score: number; // 0-100
  confidence: number; // 0-1
  source: string;
  lastUpdated: number;
  status: 'new' | 'updated' | 'stable';
  impact: number; // 0-1
}

export interface LivingSwot {
  id: string;
  ventureId: string;
  strengths: SwotElement[];
  weaknesses: SwotElement[];
  opportunities: SwotElement[];
  threats: SwotElement[];
  narrative?: string;
  lastUpdated: number;
  version: number;
}

export interface SwotHistory {
  id: string;
  timestamp: number;
  diff: {
    added: string[];
    removed: string[];
    updated: string[];
  };
  snapshot: LivingSwot;
}

export interface BusinessIdea {
  id?: string;
  uuid?: string;
  title: string;
  description: string;
  category: string;
  date?: string;
  hubOrigin: 'discovery' | 'launchpad' | 'optimize';
  riskLevel?: 'Low' | 'Medium' | 'High';
  growthPotential?: string;
  tags?: string[];
  // Scrutiny Deck ID
  scrutinyId?: string;
  // Metrics for visualization
  marketDemandScore?: number; // 0-100
  saturationIndex?: number; // 0-100
  // Launch Math specific data
  fullPath?: FinancialPath;
  leanPath?: FinancialPath;
  // RiskGuard details
  riskGuard?: RiskGuardAnalysis;
  // GapFinder/Competition details
  competition?: CompetitorComparison;
  // General metrics
  estimatedStartupCost?: string;
  potentialROI?: string;
  // Discovery & Density specific
  densityInsight?: string;
  locationName?: string;
  culturalSignificance?: string;
  temporalTrend?: string;
  historicalTrendData?: { period: string; intensity: number }[]; // 3-year history
  context?: string;
  marketAnalysis?: string;
  strategicRoadmap?: string[];
  // Pinpoint location suggestions
  siteRecommendations?: SiteRecommendation[];
  // Real-world entities detected in hotspots
  spottedEntities?: SpottedEntity[];
  // Grounding sources specific to this idea
  sources?: { title: string; uri: string }[];
  // New Hubs
  intelligenceHub?: IntelligenceHubData;
  operationsHub?: OperationsHubData;
  collaborationHub?: CollaborationHubData;
  riskGuardDeepDive?: RiskGuardDeepDive;
  launchMathDeepDive?: LaunchMathDeepDive;
  siteReconDeepDive?: SiteReconDeepDive;
  scalingStrategyDeepDive?: ScalingStrategyDeepDive;
  // Enterprise Features
  comments?: Comment[];
  snapshots?: BusinessIdeaSnapshot[];
  privacy?: 'private' | 'shared' | 'public';
  actuals?: VentureActuals;
  integrations?: IntegrationStatus;
}

export interface AnalysisResult {
  summary: string;
  ideas: BusinessIdea[];
  sources?: { title: string; uri: string }[];
  heatmapData?: HeatmapPoint[]; // Added for density visualization
}

export interface RecentScan {
  id: string;
  location: string;
  sector?: string;
  type: FeatureType;
  timestamp: number;
}

export interface ComparisonAnalysis {
  summary: string;
  gaps: string[];
  risks: string[];
  recommendation: string;
  winningVenture: string;
  selectionReasoning: string;
  comparisonMatrix: {
    feature: string;
    values: { [title: string]: string };
  }[];
}

export interface OptimizeBusiness {
  id: string;
  sourceVentureId?: string; // For migrations from Deployment/Discovery
  linkedVentures?: string[]; // For multi-input
  title: string;
  category: string;
  location: string;
  connectionStatus: 'connected' | 'pending' | 'disconnected';
  performanceData: {
    revenue: { period: string; projected: number; actual: number }[];
    expenses: { period: string; projected: number; actual: number }[];
    customers: { period: string; projected: number; actual: number }[];
  };
  liveSwot: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  historicalFeasibilitySnapshot?: Partial<BusinessIdea>;
  growthMath?: {
    expansionCapex: string;
    franchiseScenarios: string;
    equipmentUpgrades: string;
    pricingExperiments: string;
  };
  strategySandbox?: {
    newLocations: string;
    productLines: string;
    acquisitions: string;
    pivots: string;
    automationInvestments: string;
  };
  exitArchitect?: {
    valuationModeling: string;
    acquirerMapping: string;
  };
  timestamp: number;
}

export interface AppState {
  // Launchpad State
  launchpadLocation: string;
  launchpadSector: string;
  
  // Discovery Hub State
  discoveryLocation: string;
  discoveryLocality: string;
  densitySector: string;
  discoveryCountry: string;
  
  // Optimize Hub State
  optimizeBusinesses: OptimizeBusiness[];
  
  // Persistence
  recentScans: RecentScan[];
  
  // Global App State
  preferredCurrency: string;
  preferredLanguage: string;
  budgetLimit: number;
  isBudgetActive: boolean;
  skills: string;
  coords: { lat: number; lng: number } | null;
  activeFeature: FeatureType | null;
  loading: boolean;
  result: AnalysisResult | null;
  error: string | null;
  isRevealed: boolean;
  isScrutinyActive: boolean;
}

export enum ScrutinyExperience {
  FirstTime = 'first-time',
  SomeExperience = 'some-experience',
  SerialFounder = 'serial-founder'
}

export enum ScrutinyTeamSize {
  Solo = 'solo',
  Small = '2-5',
  Medium = '6-15',
  Large = '16+'
}

export enum ScrutinyTimeline {
  Immediate = '0-3 months',
  ShortTerm = '3-6 months',
  MediumTerm = '6-12 months',
  LongTerm = '12+ months'
}

export enum ScrutinyAssets {
  None = 'none',
  SomeCapital = 'some capital',
  Equipment = 'equipment',
  CustomerBase = 'prior customer base'
}

export interface ScrutinyInput {
  ventureDescription: string;
  targetSector: string;
  otherSector?: string;
  targetLocation: string;
  estimatedCapital: number;
  currency: string;
  experience: ScrutinyExperience;
  teamSize: ScrutinyTeamSize;
  launchTimeline: ScrutinyTimeline;
  existingAssets: ScrutinyAssets;
}

export interface ScrutinyGap {
  id: string;
  name: string;
  severity: 'fatal' | 'major' | 'minor';
  userAssumption: string;
  contradictingData: string;
  quantifiedGap?: string;
  source: string;
}

export interface ScrutinyAssumption {
  id: string;
  assumption: string;
  whyUnsupported: string;
  relevantData: string;
}

export interface ScrutinyPivot {
  id: string;
  description: string;
  estimatedCapital: string;
  estimatedTimeline: string;
  confidenceScore: number;
  eliminatesGaps: string[];
}

export interface ScrutinyAnalysis {
  fragilityIndex: number;
  confidence: number;
  generatedAt: number;
  modelVersion: string;
  criticalGaps: ScrutinyGap[];
  hiddenAssumptions: ScrutinyAssumption[];
  viablePivots: ScrutinyPivot[];
  recommendedNextStep: {
    action: string;
    hubDestination: 'discovery' | 'launchpad' | 'optimize';
    rationale: string;
  };
}

export interface ScrutinyDocument {
  id?: string;
  submittedAt: number;
  inputs: ScrutinyInput;
  analysis: ScrutinyAnalysis;
  userActions: {
    dismissed: boolean;
    dismissedAt?: number;
    savedToWarRoom: boolean;
    sentToHub?: string;
    exportedAt?: number;
  };
}

// ==========================================
// COOKIE & COMPLIANCE CONSENT TYPES
// ==========================================

export interface CookieConsent {
  essential: boolean;
  functional?: boolean;
  analytics?: boolean;
  aiPerformance?: boolean;
  marketing?: boolean;
  preferences?: boolean;
  region?: 'EU' | 'GLOBAL' | string;
  timestamp?: number;
}

export const defaultConsent: CookieConsent = {
  essential: true,
  functional: true,
  analytics: true,
  aiPerformance: true,
  marketing: true,
  preferences: true,
  region: 'GLOBAL',
};

export const euDefaultConsent: CookieConsent = {
  essential: true,
  functional: false,
  analytics: false,
  aiPerformance: false,
  marketing: false,
  preferences: false,
  region: 'EU',
};

export type LandingLoginSource = 'launchpad' | 'discovery' | 'optimize' | 'scrutiny' | 'hero_start_free';
